/**
 * Health Check Detail Component
 *
 * Displays detailed information for a single health check.
 */

import type { HealthCheckResult } from '../../../utils/health-api';
import { getStatusTextColor } from '../../../utils/health-api';
import { StatusIndicator } from '../../atoms/StatusIndicator';
import { Badge } from '../../atoms/Badge';
import { Text } from '../../atoms/Text';
import { Stack } from '../../layout/Stack';

export interface HealthCheckDetailProps {
  check: HealthCheckResult;
}

const statusMap: Record<string, 'pass' | 'warn' | 'fail' | 'unknown'> = {
  pass: 'pass',
  warn: 'warn',
  fail: 'fail',
};

export function HealthCheckDetail({ check }: HealthCheckDetailProps) {
  const statusVariant = statusMap[check.status] || 'unknown';

  return (
    <div className="py-3 px-4 border-b border-border last:border-b-0 hover:bg-muted/5 transition-colors">
      <Stack direction="horizontal" spacing={3} align="start">
        <StatusIndicator
          status={statusVariant}
          size="sm"
          label={`Status: ${check.status}`}
          className="mt-1"
        />

        <Stack spacing={2} className="flex-1 min-w-0">
          <Stack direction="horizontal" spacing={2} align="center" className="flex-wrap">
            <Text variant="body" className="text-sm font-medium truncate">
              {check.name}
            </Text>
            {check.critical && <Badge variant="danger" size="sm">Critical</Badge>}
            {check.category && <Badge variant="default" size="sm">{check.category}</Badge>}
          </Stack>

          <Text variant="small" className="truncate">{check.id}</Text>

          {check.output && (
            <Text variant="body" className={`text-sm ${getStatusTextColor(check.status)}`}>
              {check.output}
            </Text>
          )}

          <Stack direction="horizontal" spacing={4} className="flex-wrap">
            {check.duration !== undefined && (
              <Text variant="small">Duration: {check.duration}ms</Text>
            )}
            {check.observedValue !== undefined && check.observedUnit && (
              <Text variant="small">Value: {check.observedValue} {check.observedUnit}</Text>
            )}
            {check.timestamp && (
              <Text variant="small">{new Date(check.timestamp).toLocaleTimeString()}</Text>
            )}
          </Stack>
        </Stack>
      </Stack>
    </div>
  );
}

export default HealthCheckDetail;
